import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { IFinancialLineItemExtendedDTO } from '@modeso/types__aumico-lib-settings-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { financialLineItemActions } from '../../actions/settings.actions';
import { FinancialLineItemFormDialog } from '../../dialogs/financial-line-item-form/financial-line-item-form.dialog';
import { SettingsProvider } from '../../providers/settings.provider';
import { getDebug, getTag } from '../../utils/debugUtil';

const filename = 'projects/aumico-lib-settings-fe/src/lib/pages/financial-line-item/financial-line-item.page.ts';
const debug = getDebug(filename);
const TAG = getTag(filename);
@UntilDestroy()
@Component({
    // tslint:disable-next-line: component-selector
    selector: 'settings-financial-line-item',
    templateUrl: './financial-line-item.page.html',
    styleUrls: ['./financial-line-item.page.scss'],
})
export class FinancialLineItemPage implements OnInit, OnDestroy {
    public data: IFinancialLineItemExtendedDTO[];
    private currentFinancialLineItem: IFinancialLineItemExtendedDTO;

    private errorMessage = new Subject<HttpErrorResponse>();

    private destroy$ = new Subject<void>();

    @Input() public set injectedProviderSetter(injectedProvider: SettingsProvider) {
        if (injectedProvider) {
            this.provider = injectedProvider;
        }
    }

    @Input() languages: string[] = ['de-ch', 'en-us'];

    constructor(
        private provider: SettingsProvider,
        private dialog: MatDialog,
        private dialogRef: MatDialogRef<FinancialLineItemFormDialog>,
        protected actionsListener$: ActionsSubject,
    ) {}

    private subscriptions: Subscription[];
    ngOnInit() {
        this.subscriptions = [];
        this.subscriptions.push(
            this.provider.getActiveFinancialLineItem$().subscribe({
                next: (item: IFinancialLineItemExtendedDTO) => {
                    this.currentFinancialLineItem = item;
                },
            }),
        );

        this.subscriptions.push(
            this.actionsListener$
                .pipe(ofType(financialLineItemActions.onSubmitCreateFinancialLineItemSuccess), untilDestroyed(this))
                .subscribe(() => this.onSaveSuccessfully()),
        );

        this.subscriptions.push(
            this.actionsListener$
                .pipe(ofType(financialLineItemActions.onSubmitEditFinancialLineItemSuccess), untilDestroyed(this))
                .subscribe(() => this.onSaveSuccessfully()),
        );

        this.subscriptions.push(
            this.provider.allFinancialLineItem$().subscribe({ next: (data) => (this.data = data) }),
        );
        this.provider.dispatchGetAllFinancialLineItem();

        this.provider.financialLineItemError$().pipe(untilDestroyed(this)).subscribe(this.errorMessage);
    }

    public editPressedEventHandler(item: string): void {
        this.provider.dispatchSetActiveFinancialLineItem(item);
        this.openDialog();
    }

    onCreatePressed() {
        this.provider.dispatchSetActiveFinancialLineItem(null);
        this.openDialog();
    }

    openDialog(): void {
        this.dialogRef = this.dialog.open(FinancialLineItemFormDialog, {
            width: '70%',
            data: { financialLineItem: this.currentFinancialLineItem, errorMessage: this.errorMessage },
        });
        this.dialogRef.componentInstance.languages = this.languages;
        this.dialogRef.componentInstance.savePressed.subscribe((result) => {
            if (result._id == null) {
                this.provider.dispatchSubmitCreateFinancialLineItem(result);
            } else {
                this.provider.dispatchSubmitUpdateFinancialLineItem({ id: result._id, payload: result });
            }
        });

        this.dialogRef.afterClosed().subscribe(() => {
            // destroying subscriptions that were created upon opening the dialog
            this.destroyDialogSubscriptions();
        });
    }

    private onSaveSuccessfully() {
        this.dialogRef.close();
        Swal.fire({
            icon: 'info',
            title: 'done editing/creating',
            showCloseButton: true,
            timer: 2000,
        });
        this.provider.dispatchGetAllFinancialLineItem();
    }

    destroyDialogSubscriptions(): void {
        this.destroy$.next();
    }

    ngOnDestroy(): void {
        this.destroyDialogSubscriptions();
        this.destroy$.complete();
        this.subscriptions.forEach((subscribe) => {
            subscribe.unsubscribe();
        });
    }
}
